import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Select from "react-select";
import { createMaster, getMasterList } from "reduxStore/action/masterActions";
import strings from "translate/language";
import { errorToast, successToast } from "utils/httpClient";
import { handleListResponse, seo } from "utils/utils";
import Breadcrumb from "component/common/breadcrumb";
import TableDataNotFound from "component/TableDataNotFound";
import usePermission from "utils/usePermission";
import { MasterType } from "utils/constants";
import "./MasterListing.css";

const EMPTY_MASTER_FORM = {
  title: "",
  type: "",
  status: "",
  master_id: "",
  update: false,
};

const LOCKED_MASTER_TYPES = new Set([1, 2]);

const selectPortalProps = {
  menuPortalTarget: typeof document !== "undefined" ? document.body : null,
  menuPosition: "fixed",
  styles: {
    menuPortal: (base) => ({ ...base, zIndex: 10060 }),
  },
};

const buildMasterTypeOptions = ({ excludeLocked = false } = {}) =>
  Object.entries(MasterType)
    .filter(([key]) => {
      const typeKey = Number(key);
      if (typeKey === 0) return false;
      if (excludeLocked && LOCKED_MASTER_TYPES.has(typeKey)) return false;
      return true;
    })
    .map(([key, value]) => ({
      value: String(key),
      label: value,
    }));

const STATUS_OPTIONS = [
  { value: "Active", label: strings.active },
  { value: "Deactive", label: strings.deactive },
];

const Master = () => {
  const [searchForm, setSearchForm] = useState({
    type: "",
    search_name: "",
  });
  const [masterForm, setMasterForm] = useState(EMPTY_MASTER_FORM);
  const [formVaild, setFormVaild] = useState(false);
  const [masterData, setMasterData] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [listFilter, setListFilter] = useState("");

  const dispatch = useDispatch();
  const masterListSelector = useSelector((state) => state.masterData) || [];

  const { create, edit } = usePermission({
    create: "add_master",
    edit: "edit_master",
  });

  const filterTypeOptions = useMemo(() => buildMasterTypeOptions(), []);
  const modalTypeOptions = useMemo(
    () => buildMasterTypeOptions({ excludeLocked: true }),
    []
  );

  const groupedMasters = useMemo(() => {
    const q = listFilter.trim().toLowerCase();
    const byType = masterData.reduce((acc, item) => {
      const typeKey = Number(item.type);
      if (!acc[typeKey]) acc[typeKey] = [];
      acc[typeKey].push(item);
      return acc;
    }, {});

    const typeKeys = Object.keys(byType)
      .map(Number)
      .sort((a, b) => a - b);

    const rows = [];
    typeKeys.forEach((typeKey) => {
      const typeLabel = MasterType[typeKey] || strings.notavailable;
      const items = [...byType[typeKey]].sort((a, b) =>
        String(a.title || "").localeCompare(String(b.title || ""))
      );
      const visibleItems = q
        ? items.filter(
            (item) =>
              String(item.title || "")
                .toLowerCase()
                .includes(q) || String(typeLabel).toLowerCase().includes(q)
          )
        : items;

      if (!visibleItems.length) return;

      rows.push({
        kind: "group",
        key: `group-${typeKey}`,
        typeKey,
        typeLabel,
        count: visibleItems.length,
      });

      visibleItems.forEach((item) => {
        rows.push({
          kind: "item",
          key: item._id,
          item,
          typeKey,
          typeLabel,
        });
      });
    });

    return rows;
  }, [masterData, listFilter]);

  const flatItemsForEmpty = useMemo(
    () => groupedMasters.filter((row) => row.kind === "item").map((row) => row.item),
    [groupedMasters]
  );

  useEffect(() => {
    dispatch(getMasterList());
  }, []);

  useEffect(() => {
    if (masterListSelector?.create === true) {
      if (masterListSelector?.response?.status === 200) {
        setMasterForm(EMPTY_MASTER_FORM);
        setModalOpen(false);
        dispatch(getMasterList());
        successToast(masterListSelector?.response?.message);
      } else {
        errorToast(masterListSelector?.response?.message);
      }
      return;
    }
    if (masterListSelector?.response?.status === 200) {
      const list = masterListSelector?.response?.data || [];
      setMasterData(handleListResponse(list) ? list : []);
    } else {
      setMasterData([]);
    }
  }, [masterListSelector]);

  useEffect(() => {
    seo({
      title: "Master Listing",
      metaDescription: "Master listing",
      canonicalLink: window.location.href,
    });
  }, []);

  const handleSearchInput = (e) => {
    setSearchForm({
      ...searchForm,
      [e.target.name]: e.target.value,
    });
  };

  const handleMasterInput = (e) => {
    setMasterForm({
      ...masterForm,
      [e.target.name]: e.target.value,
    });
  };

  const handleSearchTypeChange = (selected) => {
    setSearchForm({
      ...searchForm,
      type: selected?.value || "",
    });
  };

  const handleModalTypeChange = (selected) => {
    setMasterForm({
      ...masterForm,
      type: selected?.value || "",
    });
  };

  const handleModalStatusChange = (selected) => {
    setMasterForm({
      ...masterForm,
      status: selected?.value || "",
    });
  };

  const filterDetail = () => {
    dispatch(
      getMasterList({
        type: searchForm.type,
        search_name: searchForm.search_name,
      })
    );
  };

  const filterReset = () => {
    setSearchForm({
      type: "",
      search_name: "",
    });
    setListFilter("");
    dispatch(getMasterList());
  };

  const openAddModal = () => {
    setFormVaild(false);
    setMasterForm(EMPTY_MASTER_FORM);
    setModalOpen(true);
  };

  const openEditModal = (id = "") => {
    setFormVaild(false);
    const filtered = masterData.filter((item) => item._id === id);
    if (!filtered.length) return;
    const item = filtered[0];
    if (LOCKED_MASTER_TYPES.has(Number(item.type))) return;
    setMasterForm({
      master_id: id,
      title: item.title,
      type: String(item.type),
      status: item.status ? "Active" : "Deactive",
      update: true,
    });
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setFormVaild(false);
    setMasterForm(EMPTY_MASTER_FORM);
  };

  const handleSubmit = () => {
    if (masterForm.title === "") {
      setFormVaild(true);
      return;
    }
    setFormVaild(false);
    const payload = {
      title: masterForm.title,
      type: masterForm.type,
      status: masterForm.status === "Active",
    };
    if (masterForm.master_id) {
      payload.master_id = masterForm.master_id;
    }
    dispatch(createMaster(payload));
  };

  const canEditMaster = (item) =>
    edit && !LOCKED_MASTER_TYPES.has(Number(item.type));

  const selectedSearchType =
    filterTypeOptions.find((item) => item.value === String(searchForm.type)) ||
    null;

  const selectedModalType =
    modalTypeOptions.find((item) => item.value === String(masterForm.type)) ||
    null;

  const selectedModalStatus =
    STATUS_OPTIONS.find((item) => item.value === masterForm.status) || null;

  return (
    <div className="page-body justo-master-page">
      <Breadcrumb title={strings.master} titleactive={strings.masterListing} />

      <div className="container-fluid justo-master">
        <div className="justo-master__card">
          <div className="justo-master__top">
            <div>
              <h2 className="justo-master__title">{strings.listOfMaster}</h2>
              <p className="justo-master__subtitle">
                {masterData.length} masters · grouped by type · scroll inside the
                table panel.
              </p>
            </div>
            {create ? (
              <div className="justo-master__top-actions">
                <button
                  type="button"
                  className="justo-master__btn justo-master__btn--solid"
                  onClick={openAddModal}
                >
                  <i className="fa fa-plus" aria-hidden="true" />
                  {strings.addNewMaster}
                </button>
              </div>
            ) : null}
          </div>

          <div className="justo-master__filters">
            <div className="justo-master__filters-grid">
              <div className="justo-master__field">
                <label className="justo-master__label" htmlFor="search_name">
                  {strings.searchByTitle}
                </label>
                <input
                  id="search_name"
                  className="justo-master__input"
                  type="text"
                  name="search_name"
                  value={searchForm.search_name}
                  onChange={handleSearchInput}
                  placeholder={strings.searchTitle}
                  autoComplete="off"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") filterDetail();
                  }}
                />
              </div>
              <div className="justo-master__field">
                <label className="justo-master__label">
                  {strings.searchByType}
                </label>
                <Select
                  classNamePrefix="justo-master-select"
                  isSearchable
                  isClearable
                  placeholder={strings.selectType}
                  options={filterTypeOptions}
                  value={selectedSearchType}
                  onChange={handleSearchTypeChange}
                  {...selectPortalProps}
                />
              </div>
              <div className="justo-master__field">
                <label className="justo-master__label" htmlFor="list_filter">
                  Filter visible
                </label>
                <input
                  id="list_filter"
                  className="justo-master__input"
                  type="text"
                  value={listFilter}
                  onChange={(e) => setListFilter(e.target.value)}
                  placeholder="Type to narrow groups"
                  autoComplete="off"
                />
              </div>
            </div>
            <div className="justo-master__filter-actions">
              <button
                type="button"
                className="justo-master__btn justo-master__btn--solid"
                onClick={filterDetail}
              >
                <i className="fa fa-search" aria-hidden="true" />
                {strings.search}
              </button>
              <button
                type="button"
                className="justo-master__btn justo-master__btn--ghost"
                onClick={filterReset}
              >
                {strings.reset}
              </button>
            </div>
          </div>

          <div className="justo-master__scroll">
            <table className="justo-master__table">
              <thead>
                <tr>
                  <th scope="col">{strings.title}</th>
                  <th scope="col">{strings.type}</th>
                  <th className="justo-master__col-status" scope="col">
                    {strings.status}
                  </th>
                  {edit ? (
                    <th className="justo-master__col-action" scope="col">
                      {strings.action}
                    </th>
                  ) : null}
                </tr>
              </thead>
              <tbody>
                {groupedMasters.map((row) => {
                  if (row.kind === "group") {
                    return (
                      <tr key={row.key} className="justo-master__row--group">
                        <td>
                          <div className="justo-master__title-cell">
                            <span
                              className="justo-master__badge"
                              aria-hidden="true"
                            >
                              T
                            </span>
                            <span className="justo-master__name justo-master__name--group">
                              {row.typeLabel}
                            </span>
                          </div>
                        </td>
                        <td>
                          <span className="justo-master__count">
                            {row.count}
                          </span>
                        </td>
                        <td className="justo-master__col-status">
                          <span className="justo-master__action-empty">—</span>
                        </td>
                        {edit ? (
                          <td className="justo-master__col-action">
                            <span className="justo-master__action-empty">—</span>
                          </td>
                        ) : null}
                      </tr>
                    );
                  }

                  const { item } = row;
                  const isActive = !!item.status;
                  return (
                    <tr key={row.key} className="justo-master__row--item">
                      <td>
                        <div className="justo-master__title-cell justo-master__title-cell--child">
                          <span
                            className="justo-master__badge justo-master__badge--child"
                            aria-hidden="true"
                          >
                            M
                          </span>
                          <span className="justo-master__name justo-master__name--child">
                            {item.title}
                          </span>
                        </div>
                      </td>
                      <td className="justo-master__type">{row.typeLabel}</td>
                      <td className="justo-master__col-status">
                        <span
                          className={`justo-master__status${
                            isActive
                              ? " justo-master__status--active"
                              : " justo-master__status--inactive"
                          }`}
                        >
                          <i
                            className={`fa ${
                              isActive
                                ? "fa-check-circle"
                                : "fa-exclamation-circle"
                            }`}
                            aria-hidden="true"
                          />
                          {isActive ? strings.active : strings.deactive}
                        </span>
                      </td>
                      {edit ? (
                        <td className="justo-master__col-action">
                          {canEditMaster(item) ? (
                            <div className="justo-master__actions">
                              <button
                                type="button"
                                className="justo-master__btn justo-master__btn--outline justo-master__btn--sm"
                                onClick={() => openEditModal(item._id)}
                              >
                                <i
                                  className="fa fa-pencil"
                                  aria-hidden="true"
                                />
                                {strings.edit}
                              </button>
                            </div>
                          ) : (
                            <span className="justo-master__action-empty">—</span>
                          )}
                        </td>
                      ) : null}
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <TableDataNotFound data={flatItemsForEmpty} />
          </div>
        </div>
      </div>

      {modalOpen ? (
        <div
          className="justo-master__modal-backdrop"
          role="presentation"
          onClick={closeModal}
        >
          <div
            className="justo-master__modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="master-modal-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="justo-master__modal-head">
              <div>
                <h3
                  className="justo-master__modal-title"
                  id="master-modal-title"
                >
                  {masterForm.update ? strings.edit : strings.addNewMaster}
                </h3>
                <p className="justo-master__modal-subtitle">
                  {masterForm.update
                    ? "Update master title, type, and status."
                    : "Create a new master entry for dropdowns and filters."}
                </p>
              </div>
              <button
                type="button"
                className="justo-master__modal-close"
                aria-label={strings.cancel}
                onClick={closeModal}
              >
                ×
              </button>
            </div>

            <div className="justo-master__modal-body">
              <div className="justo-master__modal-field">
                <label className="justo-master__modal-label">
                  {strings.typeOfMaster}
                </label>
                <Select
                  classNamePrefix="justo-master-select"
                  isSearchable
                  isClearable
                  placeholder={strings.selectType}
                  options={modalTypeOptions}
                  value={selectedModalType}
                  onChange={handleModalTypeChange}
                  {...selectPortalProps}
                />
              </div>

              <div className="justo-master__modal-field">
                <label
                  className="justo-master__modal-label"
                  htmlFor="master_title"
                >
                  {strings.title}
                </label>
                <input
                  id="master_title"
                  className="justo-master__modal-input"
                  type="text"
                  autoComplete="off"
                  placeholder={strings.title}
                  name="title"
                  value={masterForm.title}
                  onChange={handleMasterInput}
                />
                {formVaild ? (
                  <span className="justo-master__modal-error">
                    {strings.titleisrequired}
                  </span>
                ) : null}
              </div>

              <div className="justo-master__modal-field">
                <label className="justo-master__modal-label">
                  {strings.status}
                </label>
                <Select
                  classNamePrefix="justo-master-select"
                  isSearchable
                  isClearable
                  placeholder={strings.selectstatus}
                  options={STATUS_OPTIONS}
                  value={selectedModalStatus}
                  onChange={handleModalStatusChange}
                  {...selectPortalProps}
                />
              </div>
            </div>

            <div className="justo-master__modal-foot">
              <button
                type="button"
                className="justo-master__btn justo-master__btn--ghost"
                onClick={closeModal}
              >
                {strings.cancel}
              </button>
              <button
                type="button"
                className="justo-master__btn justo-master__btn--solid"
                onClick={handleSubmit}
              >
                <i className="fa fa-check" aria-hidden="true" />
                {masterForm.update ? strings.update : strings.submit}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default Master;
